import { useMemo } from 'react';
import MetricWidget from '../../features/metrics/components/MetricWidget/MetricWidget';
import { LocationMapWidget } from '../../components/Organisms/LocationMapWidget/LocationMapWidget';
import DateRangeList from '../../features/dateRanges/components/DateRangeList';
import TooltipButton from '../../components/Atoms/TooltipButton/TooltipButton';
import { KpiWidgetsConfig } from './Dashboard.utils';
import { MetricIdEnum, WidgetType } from '../../features/metrics/Metrics.model';
import * as Styled from './Dashboard.styles';
import { WidgetColumn } from './Dashboard.model';
import { MdLink } from 'react-icons/md';
import { AMPECO_BASE_URL, PARTNER_ADMIN_LOCATION_THRESHOLD } from '../../config';
import UtilizationMetricWidget from '../../features/metrics/components/UtilizationMetricWidget/UtilizationMetricWidget';
import { LocationFilterProvider } from '../../features/locations/components/LocationFilter/LocationFilter.context';
import { useLocations } from '../../features/locations/Location.context';

const Dashboard = () => {
  const { locations } = useLocations();

  // Temporarily logic to only show time-based utilization when there are more than X locations in testing purposes
  const shouldShowTimeBasedUtilization = locations && locations.length > Number.parseInt(PARTNER_ADMIN_LOCATION_THRESHOLD, 10);
  const widgetsConfig = useMemo(() => {
    if (shouldShowTimeBasedUtilization) {
      return KpiWidgetsConfig;
    }

    return KpiWidgetsConfig
      .filter(node => node.widget.metricId !== MetricIdEnum.timeBasedUtilization)
      .map(node => node.widget.widgetType === WidgetType.MAP ? { ...node, span: 4 } : node);
  }, [shouldShowTimeBasedUtilization]);


  const isUtilizationMetric = (metricId?: MetricIdEnum) =>
    metricId === MetricIdEnum.avgStationUtilization ||
    metricId === MetricIdEnum.timeBasedUtilization;

  const renderWidget = (node: WidgetColumn) => {
    const { widget } = node;

    if (widget.widgetType === WidgetType.MAP) {
      return <LocationMapWidget />;
    } else if (isUtilizationMetric(widget.metricId)) {
      return (
        <LocationFilterProvider>
          <UtilizationMetricWidget
            metricId={widget.metricId}
            type={widget.widgetType}
            hasLocationFilter={widget.hasLocationFilter}
            animate
          />
        </LocationFilterProvider>
      );
    } else if (widget.metricId) {
      return (
        <MetricWidget
          metricId={widget.metricId}
          type={widget.widgetType}
          animate
        />
      );
    }

    return null;
  };

  const widgetList = (items: WidgetColumn[]) => {
    return items.map((node, i) => (
      <Styled.WidgetGridContainer key={`widgets-${i}`} span={node.span}>
        {renderWidget(node)}
      </Styled.WidgetGridContainer>
    ));
  };

  return (
    <Styled.DashboardContainer>
      <Styled.DashboardActions>
        <DateRangeList />
        <TooltipButton
          selected={false}
          onClick={() => {
            if (window.top) {
              window.top.location.href = `${AMPECO_BASE_URL}/admin/nova-reports`;
            }
          }}
          icon={<MdLink />}
          label='Go to Analytics'
          description='View detailed reports'
          placement='top'
          color='secondary'
          disable={!AMPECO_BASE_URL}
        />
      </Styled.DashboardActions>
      <Styled.WidgetGrid>{widgetList(widgetsConfig)}</Styled.WidgetGrid>
    </Styled.DashboardContainer>
  );
};

export default Dashboard;
